// Design tokens for the redesigned dialog (components/RedesignDialog), the
// dialog of the redesigned views (expense tracking, the projects page, the
// project bank).
//
// A factory whose every argument is required, for the reason spelled out at the
// top of chatTokens.js: darkTheme and lightTheme are both built as
// merge({}, classicThemeVariables, {...}), so a token defined only in classic is
// silently inherited by the other two. Here a missing argument also throws as
// the theme is built, so a theme that forgets a role fails at once instead of
// painting `undefined`.
//
// ## Why the dialog has surfaces of its own
//
// The classic and the dark theme, and the project bank's tokens, are dark navy
// (#13243a, #0d1b2a); a dialog in the same navy over a navy page makes the
// whole product one blue, and a neutral graphite read too dark and grey next
// to the redesigns. The dialog is a cool slate instead: the redesigns' blue
// family, a step lighter and far less saturated than the bank's cards in the
// classic and the dark theme, white and cool greys in the light theme; its
// lines, outlines and fills carry the same faint blue cast. Lixani's cyan
// appears only as the accent (the primary action, the focus outline, a
// checked control), the status colours only where they mean something.

// ## Roles
//
// `surface` is the paper (its header, body and footer), `surfaceRaised` a bar
// or a menu of the dialog's own that has to read above it, `well` a fill
// inside it (the PDF pane of a split dialog, a group of fields). `border` is a
// line inside the dialog, `edge` the paper's own hairline, drawn outside it so
// that a pane's fill never covers it. `fieldOutline` is the outline of a field
// and of the neutral outlined button: a control's boundary, so it clears 3:1
// against the surface (MUI's 0.23 does not). The accent and its foreground,
// and each status colour and its tinted fill, are argument pairs: they are
// only correct together.

const ROLES = [
  'backdrop',
  'surface',
  'surfaceRaised',
  'well',
  'border',
  'edge',
  'text',
  'muted',
  'fieldOutline',
  'fieldOutlineHover',
  'hoverFill',
  'selectedFill',
  'accent',
  'accentText',
  'shadow',
  'successText',
  'successFill',
  'warningText',
  'warningFill',
  'dangerText',
  'dangerFill',
];

/** The arguments redesignDialogComponentTokens requires, in order. */
export const REDESIGN_DIALOG_TOKEN_ROLES = ROLES;

/**
 * Redesigned dialog tokens for theme.component.*. Every argument is required
 * (a non-empty string); a missing one throws, naming it.
 * @example
 * component: {
 *   ...redesignDialogComponentTokens({ backdrop: 'rgba(0,0,0,0.6)', surface: '#1c1e21', ... }),
 * }
 * @param {Object<string, string>} values one colour (`shadow`: a box-shadow) per role
 * @returns {Object} the redesignDialog* tokens
 */
export const redesignDialogComponentTokens = (values) => {
  const missing = ROLES.filter(
    (role) =>
      !values ||
      typeof values[role] !== 'string' ||
      values[role].trim().length === 0
  );
  if (missing.length > 0) {
    throw new Error(
      `redesignDialogComponentTokens: missing ${missing.join(', ')}`
    );
  }
  const {
    backdrop,
    surface,
    surfaceRaised,
    well,
    border,
    edge,
    text,
    muted,
    fieldOutline,
    fieldOutlineHover,
    hoverFill,
    selectedFill,
    accent,
    accentText,
    shadow,
    successText,
    successFill,
    warningText,
    warningFill,
    dangerText,
    dangerFill,
  } = values;
  return {
    redesignDialogBackdrop: { backgroundColor: backdrop },
    // The paper and its text.
    redesignDialogSurface: { backgroundColor: surface, color: text },
    redesignDialogSurfaceRaised: { backgroundColor: surfaceRaised },
    redesignDialogWell: { backgroundColor: well },
    redesignDialogBorder: { borderColor: border },
    redesignDialogEdge: { borderColor: edge },
    redesignDialogShadow: { boxShadow: shadow },
    redesignDialogMuted: { color: muted },
    redesignDialogFieldOutline: { borderColor: fieldOutline },
    redesignDialogFieldOutlineHover: { borderColor: fieldOutlineHover },
    redesignDialogHoverFill: { backgroundColor: hoverFill },
    redesignDialogSelectedFill: { backgroundColor: selectedFill },
    // Filled accents (the primary button, a checked control); the accent
    // also clears 4.5:1 on the surface, so a focused label may use it.
    redesignDialogAccent: { backgroundColor: accent, color: accentText },
    // Status text on its tinted fill (a status chip, a tonal button), 4.5:1
    // over the surface, the raised surface and the well.
    redesignDialogSuccess: { backgroundColor: successFill, color: successText },
    redesignDialogWarning: { backgroundColor: warningFill, color: warningText },
    redesignDialogDanger: { backgroundColor: dangerFill, color: dangerText },
  };
};
